import { FC, ReactNode } from 'react'

import { Card, Divider, Layout, Typography } from 'antd'
import { createStyles } from 'antd-style'

import type { DOMNode } from 'html-dom-parser'
import parse, { domToReact } from 'html-react-parser'
import slugify from 'slugify'

import { Title } from '../text/Title'

const useStyles = createStyles(({ token, css }) => ({
  paragraph: css`
    line-height: 2;
    margin-bottom: 2em;

    i {
      font-style: italic;
    }

    b {
      font-weight: bold;
    }
    
    
  `,
  blockquote: css`
    margin-bottom: 1em;
    font-style: italic;
    background: ${token.colorBgContainerDisabled};
  `,
  markup: css`
    .ant-typography li {
      margin-bottom: 1em;
      margin-left: 1em;
      list-style: outside;
    }

    ol {
      margin-left: 1em;
      margin-bottom: 2em;
      list-style: decimal outside;
      
      li {
        margin-bottom: 1em;
        line-height: 2;
      }
    }

    h2 {
      scroll-margin: 88px;
    }

    table {
      display: block;
      overflow-x: scroll;
    }

    table {
      width: 100%;
      margin: 2em 0;
    }

    table th {
      font-weight: bold;
    }

    table td,
    th {
      border: 1px solid ${token.colorBorder};
      padding: 1em;
    }
  `,
}))

const HTML: FC<{ children?: string | null }> = ({ children = '' }) => {
  const { styles } = useStyles()
  return (
    <Layout.Content className={styles.markup}>
      {parse(children ?? '', {
        trim: true,
        replace(domNode) {
          const { tagName, children: nodes } = domNode as { tagName: string; children: DOMNode[] }
          if (!nodes) return null
          const children: ReactNode = domToReact(nodes)
          switch (tagName) {
            case 'p':
              return <Typography.Paragraph className={styles.paragraph}>{children}</Typography.Paragraph>
            case 'ul':
              return <Typography.Paragraph>{children}</Typography.Paragraph>
            case 'h1':
              return (
                <Title id={slugify(children as string).toLowerCase()} level={1}>
                  {children}
                </Title>
              )
            case 'h2':
              return (
                <Title id={children ? slugify(children as string).toLowerCase() : undefined} level={2}>
                  {children}
                </Title>
              )
            case 'h3':
              return (
                <Title id={children ? slugify(children as string).toLowerCase() : undefined} level={3}>
                  {children}
                </Title>
              )
            case 'h4':
              return (
                <Title id={children ? slugify(children as string).toLowerCase() : undefined} level={4}>
                  {children}
                </Title>
              )
            case 'h5':
              return (
                <Title id={slugify(children as string).toLowerCase()} level={5}>
                  {children}
                </Title>
              )
            case 'hr':
              return <Divider />
            case 'table':
              return <div style={{ overflow: 'auto' }}>{children}</div>
            case 'span':
              return <Typography.Text>{children}</Typography.Text>
            case 'blockquote':
              return (
                <Card variant={'borderless'} className={styles.blockquote}>
                  {children}
                </Card>
              )
            default:
              return children
          }
        },
      })}
    </Layout.Content>
  )
}

export { HTML }
